import {
	useContext,
	ReactNode,
	useLayoutEffect,
	createContext,
	useState,
} from 'react';
import { useLocalStorage, useSessionStorage } from '@mantine/hooks';
import { Admin, AppUser, User, UserType } from '@/types/user';
import {
	LoginValues,
	SubmitStatus,
} from '@/components/views/unauthenticated/login/Login';
import { useQueryClient } from '@tanstack/react-query';
import { isAppError } from '@/utils/utilities';
import { AxiosError } from 'axios';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { ErrorCode, UUID } from '@/types/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppPaths } from '@/configs/appPaths';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import httpClient from '@/api/httpClient';
import StorageKey from '@/configs/storageKey';
import Cookies from 'js-cookie';
import UserApi from '@/api/UserApi';
import AdminApi from '@/api/AdminApi';

type LoginResponse =
	| { status: SubmitStatus.SUCCESS }
	| { status: SubmitStatus.ERROR; stack: ErrorCode | string };

interface TokenPayload extends JwtPayload {
	id: UUID;
	user: {
		id: UUID;
		userType: UserType;
	};
}

interface Context {
	remember: boolean;
	setRemember: (value: boolean) => void;
	user: AppUser | null;
	admin: Admin | null;
	changeUser: (newUser?: AppUser | null) => void;
	upatedUser: (data: Partial<AppUser>) => void;
	login: (values: LoginValues) => Promise<LoginResponse>;
	adminHandoff: (code: string) => Promise<void>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<Context>(null!);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const [remember, setRemember] = useLocalStorage({
		key: StorageKey.REMEMBER,
		defaultValue: true,
		getInitialValueInEffect: false,
	});

	const [user, setUser] = useLocalStorage<AppUser | null>({
		key: StorageKey.USER,
		defaultValue: null,
		getInitialValueInEffect: false,
	});

	const [admin, setAdmin] = useSessionStorage<Admin | null>({
		key: StorageKey.ADMIN,
		defaultValue: null,
		getInitialValueInEffect: false,
	});

	const [refreshTimeout, setRefreshTimeout] = useState<number>();

	useLayoutEffect(() => {
		const code = searchParams.get('code');
		if (code) {
			adminHandoff(code);
			return;
		}

		loginWithResfreshToken();
	}, []);

	const loginWithResfreshToken = async () => {
		const refresh = Cookies.get(StorageKey.REFRESH_TOKEN);

		if (!refresh) {
			setUser(null);
			setAdmin(null);
			return;
		}

		try {
			const response = await UnauthenticatedApi.refreshToken(refresh);

			const { accessToken: token, refreshToken } = response.data;

			const decoded = jwtDecode<TokenPayload>(token);

			await setAuth(
				decoded,
				token,
				refreshToken,
				decoded.user.userType === UserType.ADMIN
			);
		} catch (error: any) {
			console.error(error);
			if (error.code !== AxiosError.ECONNABORTED) logout();
		}
	};

	const refreshTokenWhenExpire = (token: TokenPayload) => {
		const tokenLifeSpan = token.iat && token.exp ? token.exp - token.iat : 0;
		// Refresh 5 sec before expire
		const refreshTimeout = (tokenLifeSpan - 5) * 1000;

		const id = setTimeout(() => {
			loginWithResfreshToken();
		}, refreshTimeout);

		setRefreshTimeout(id);
	};

	const login: Context['login'] = async ({ email, password }) => {
		try {
			const response = await UnauthenticatedApi.login({ email, password });

			const { accessToken: token, refreshToken } = response.data;
			const decoded = jwtDecode<TokenPayload>(token);

			if (decoded.user.userType !== UserType.USER)
				return {
					stack: ErrorCode.USER_NOT_FOUND,
					status: SubmitStatus.ERROR,
				};

			await setAuth(decoded, token, refreshToken);

			return { status: SubmitStatus.SUCCESS };
		} catch (error) {
			const stack = isAppError(error) ? error.stack : ErrorCode.GENERIC;
			return { stack, status: SubmitStatus.ERROR };
		}
	};

	const adminHandoff: Context['adminHandoff'] = async (code) => {
		logout();

		try {
			const response = await UnauthenticatedApi.handoff({ code });

			const { accessToken: token, refreshToken } = response.data;
			const decoded = jwtDecode<TokenPayload>(token);

			await setAuth(decoded, token, refreshToken, true);
		} catch (error) {
			navigate('/');
		}
	};

	const logout: Context['logout'] = async () => {
		queryClient.cancelQueries();
		Cookies.remove(StorageKey.REFRESH_TOKEN);
		httpClient.defaults.headers.common['Authorization'] = '';
		setUser(null);
		setAdmin(null);
		queryClient.clear();
		clearTimeout(refreshTimeout);
	};

	const setAuth = async (
		decoded: TokenPayload,
		token: string,
		refreshToken: string,
		isAdmin: boolean = false
	) => {
		setAuthHeader(token);

		const appUser = isAdmin
			? await AdminApi.mutationGetOne(decoded.user.id)
			: await UserApi.mutationGetOne(decoded.user.id);

		Cookies.set(StorageKey.REFRESH_TOKEN, refreshToken, {
			secure: true,
			expires: remember ? 360 : undefined,
		});

		refreshTokenWhenExpire(decoded);

		if (isAdmin) {
			const investmentItemId = searchParams.get('investmentItemId'),
				productTypeId = searchParams.get('productTypeId'),
				projectId = searchParams.get('projectId');

			setAdmin(appUser as Admin);

			if (investmentItemId && productTypeId && projectId)
				navigate(
					`/${AppPaths.CREATOR}/${investmentItemId}/${productTypeId}/${projectId}`
				);
		} else setUser(appUser as User);
	};

	const changeUser: Context['changeUser'] = (newUser = null) =>
		setUser(newUser);

	const upatedUser: Context['upatedUser'] = (data) =>
		setUser((prev) => (prev ? { ...prev, ...data } : null));

	return (
		<AuthContext.Provider
			value={{
				remember,
				setRemember,
				user,
				admin,
				changeUser,
				upatedUser,
				login,
				adminHandoff,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

function setAuthHeader(token: string) {
	httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}
